import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { AlertTriangle, X, Info } from "lucide-react";

export type ValidationError = {
  row: number;
  field: string;
  value: string;
  error: string;
};

type Props = {
  errors: ValidationError[];
  onClose: () => void;
};

export function DateFormatErrorModal({ errors, onClose }: Props) {
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "grey.100" }}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "error.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={16} color="#b91c1c" />
          </Box>
          <Box flex={1}>
            <Typography fontSize={14} fontWeight={500} color="text.primary">
              Unsupported date format found
            </Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.25}>
              {errors.length} error{errors.length > 1 ? "s" : ""} in the uploaded file.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "text.disabled" }}>
            <X size={16} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "text.disabled", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, width: 48 }}>Row</TableCell>
              <TableCell sx={{ color: "text.disabled", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, width: 100 }}>Field</TableCell>
              <TableCell sx={{ color: "text.disabled", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Invalid value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((e, i) => (
              <TableRow key={i}>
                <TableCell sx={{ color: "text.disabled", fontSize: 12 }}>{e.row}</TableCell>
                <TableCell>
                  <Chip
                    label={e.field}
                    size="small"
                    sx={{ bgcolor: "grey.100", color: "text.secondary", fontSize: 11, fontWeight: 500, height: 22 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<X size={11} color="#b91c1c" />}
                    label={e.value}
                    size="small"
                    sx={{ bgcolor: "#fef2f2", color: "#b91c1c", fontSize: 11, fontWeight: 500, height: 22 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Hint */}
        <Box
          sx={{
            mt: 2,
            bgcolor: "#f0fdf4",
            borderRadius: 1.5,
            p: 1.5,
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
          }}
        >
          <Info size={16} color="#15803d" style={{ marginTop: 1, flexShrink: 0 }} />
          <Typography fontSize={12} color="#166534" lineHeight={1.6}>
            Use <strong>DD-MMM-YYYY</strong> format — e.g. <strong>12-May-2026</strong>.
          </Typography>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 2.5, py: 2, borderTop: "1px solid", borderColor: "grey.100" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontSize: 13 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}